import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import masterKeyValidationAdapter from "common/Adapters/MasterKeyValidationAdapter/Router";

const initialState = {
  checked: false,
  existsOnServer: false,
  bypassed: false,
  error: null,
};

const hasRole = (roles, role) => Boolean(roles?.includes?.(role));

export function deriveEvaluationAccess({ hasInputRole, hasOutputRole, state, isLoggingOut = false, isMasterKeyRestoring, isMasterKeyValid }) {
  const ready = Boolean(!isLoggingOut && !isMasterKeyRestoring && state.checked && !state.error && hasOutputRole);

  const isCompanyMode = Boolean(ready && hasInputRole && state.existsOnServer && isMasterKeyValid && !state.bypassed);

  const isGenericMode = Boolean(ready && (!hasInputRole || !state.existsOnServer || state.bypassed) && !isMasterKeyValid);

  const shouldPrompt = Boolean(ready && hasInputRole && state.existsOnServer && !state.bypassed && !isMasterKeyValid);

  const accessMode = isCompanyMode ? "company" : isGenericMode ? "generic" : "blocked";

  return {
    isCompanyMode,
    isGenericMode,
    shouldPrompt,
    accessMode,
    canFetch: isCompanyMode || isGenericMode,
  };
}

export default function useEvaluationMasterKeyAccess(user) {
  const { isMasterKeyValid, isMasterKeyRestoring, isLoggingOut, cryptoSessionRevision, clearMasterKeySession } = useContext(MasterKeyContext);

  const [state, setState] = useState(initialState);
  const roles = user?.getRole?.() || [];
  const hasInputRole = hasRole(roles, "bm_input_role");
  const hasOutputRole = hasRole(roles, "bm_output_role");

  useEffect(() => {
    if (!user || isLoggingOut) {
      setState(initialState);
      return undefined;
    }

    if (!hasOutputRole || !hasInputRole) {
      setState({
        checked: true,
        existsOnServer: false,
        bypassed: false,
        error: null,
      });

      return undefined;
    }

    let mounted = true;
    setState(initialState);

    masterKeyValidationAdapter
      .exists()
      .then((response) => {
        if (!mounted) return;

        setState({
          checked: true,
          existsOnServer: Boolean(response?.data?.valid),
          bypassed: false,
          error: null,
        });
      })
      .catch((error) => {
        console.error("Failed to check whether an Evaluation master key exists", error);

        if (mounted) {
          setState({
            ...initialState,
            checked: true,
            error,
          });
        }
      });

    return () => {
      mounted = false;
    };
  }, [user, hasInputRole, hasOutputRole, isLoggingOut]);

  const markAuthenticated = useCallback(() => {
    setState((current) => ({
      ...current,
      checked: true,
      existsOnServer: true,
      bypassed: false,
      error: null,
    }));
  }, []);

  const continueWithoutKey = useCallback(() => {
    setState((current) => ({
      ...current,
      checked: true,
      bypassed: true,
      error: null,
    }));
  }, []);

  const requiresGenericSession = Boolean(
    hasOutputRole && state.checked && !state.error && (!hasInputRole || !state.existsOnServer || state.bypassed)
  );

  useEffect(() => {
    if (!isLoggingOut && !isMasterKeyRestoring && requiresGenericSession && isMasterKeyValid) {
      clearMasterKeySession();
    }
  }, [clearMasterKeySession, isLoggingOut, isMasterKeyRestoring, isMasterKeyValid, requiresGenericSession]);

  const access = deriveEvaluationAccess({
    hasInputRole,
    hasOutputRole,
    state,
    isLoggingOut,
    isMasterKeyRestoring,
    isMasterKeyValid,
  });

  const userScopeKey = String(user?.getSiaId?.() ?? user?.getUsername?.() ?? "anonymous");

  const dataScopeKey = `${userScopeKey}:${access.accessMode}:${cryptoSessionRevision}`;

  return useMemo(
    () => ({
      ...state,
      ...access,
      hasInputRole,
      hasOutputRole,
      dataScopeKey,
      markAuthenticated,
      continueWithoutKey,
    }),
    [
      state,
      access.isCompanyMode,
      access.isGenericMode,
      access.shouldPrompt,
      access.accessMode,
      access.canFetch,
      hasInputRole,
      hasOutputRole,
      dataScopeKey,
      markAuthenticated,
      continueWithoutKey,
    ]
  );
}
